import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { LMSCourseAttempt } from '@nsc/simple-lms/entities/LMSCourseAttempt.js';
import { LMSCourseEnrollment, LMSEnrollmentStatus } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { createLMSPayload, getABAC, LMSAction } from '@nsc/simple-lms/init.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';

/**
 * One enrolled field engineer, enriched server-side with the FE's name / email / languages so the
 * client never has to resolve them per row. Attempt data is pre-summarised into just what the
 * Enrollments table renders (status, completed scores, first-passed date, delete guard).
 *
 * @entity CourseEnrollmentRow
 */
@Entity()
export class CourseEnrollmentRow extends AdornisEntity {
  static override _class = 'CourseEnrollmentRow';

  static override resolveID(entity: CourseEnrollmentRow): string {
    return entity._id;
  }

  /** Enrollment `_id`. */
  @Field(() => ID) _id!: string;
  @Field(() => String) userId: Maybe<string>;
  @Field(() => String) feName: Maybe<string>;
  @Field(() => String) feEmail: Maybe<string>;
  @Field(() => String) feLanguages: Maybe<string>;
  /** One of {@link LMSEnrollmentStatus}. */
  @Field(() => String) status: Maybe<string>;
  @Field(() => Date) lockedAt: Maybe<Date>;
  @Field(() => Date) firstPassedAt: Maybe<Date>;
  @Field(() => Boolean) hasCompletedAttempt: Maybe<boolean>;
  /** Completed attempts only, in chronological order — carries `scorePercent` + `passed`. */
  @Field(() => [LMSCourseAttempt]) completedAttempts: Maybe<LMSCourseAttempt[]>;
  @Field(() => Date) createdAt: Maybe<Date>;

  constructor(doc: PartialEntityData<CourseEnrollmentRow>) {
    super(doc);
  }
}

/**
 * A single page of course enrollments plus the total match count, so the client can render numbered
 * pagination without a second round trip.
 *
 * @entity CourseEnrollmentPage
 */
@Entity()
export class CourseEnrollmentPage extends AdornisEntity {
  static override _class = 'CourseEnrollmentPage';

  static override resolveID(entity: CourseEnrollmentPage): string {
    return entity._id;
  }

  /** Synthetic id (the course id) so baseql has a stable identity for this value object. */
  @Field(() => ID) _id!: string;
  @Field(() => Int) total: Maybe<number>;
  @Field(() => [CourseEnrollmentRow]) rows: Maybe<CourseEnrollmentRow[]>;

  constructor(doc: PartialEntityData<CourseEnrollmentPage>) {
    super(doc);
  }
}

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

interface RawAttempt {
  scorePercent?: number | null;
  passed?: boolean | null;
  completedAt?: Date | null;
  ip?: string | null;
}

interface RawEnrollmentDoc {
  _id: string;
  userId?: string | null;
  createdAt?: Date | null;
  lockedAt?: Date | null;
  attempts?: RawAttempt[] | null;
  _fe?: {
    name?: string | null;
    email?: string | null;
    firstLanguage?: string | null;
    secondLanguage?: string | null;
  } | null;
}

const deriveStatus = (attempts: RawAttempt[]): LMSEnrollmentStatus => {
  const completed = attempts.filter(a => a.completedAt);
  if (completed.some(a => a.passed)) return LMSEnrollmentStatus.PASSED;
  if (attempts.some(a => !a.completedAt)) return LMSEnrollmentStatus.IN_PROGRESS;
  if (completed.length) return LMSEnrollmentStatus.FAILED;
  return LMSEnrollmentStatus.ASSIGNED;
};

const deriveFirstPassedAt = (attempts: RawAttempt[]): Date | null => {
  const passedDates = attempts
    .filter(a => a.passed && a.completedAt)
    .map(a => new Date(a.completedAt as Date));
  if (passedDates.length === 0) return null;
  return passedDates.reduce((earliest, current) => (current < earliest ? current : earliest));
};

const toRow = (doc: RawEnrollmentDoc): CourseEnrollmentRow => {
  const attempts = doc.attempts ?? [];
  const completed = attempts.filter(a => a.completedAt);
  const languages = [doc._fe?.firstLanguage, doc._fe?.secondLanguage].filter(Boolean).join(', ');
  return new CourseEnrollmentRow({
    _id: doc._id,
    userId: doc.userId ?? undefined,
    feName: doc._fe?.name ?? undefined,
    feEmail: doc._fe?.email ?? undefined,
    feLanguages: languages || undefined,
    status: deriveStatus(attempts),
    lockedAt: doc.lockedAt ?? undefined,
    firstPassedAt: deriveFirstPassedAt(attempts) ?? undefined,
    hasCompletedAttempt: completed.length > 0,
    completedAttempts: completed.map(
      a => new LMSCourseAttempt({ scorePercent: a.scorePercent, passed: a.passed, completedAt: a.completedAt, ip: a.ip }),
    ),
    createdAt: doc.createdAt ?? undefined,
  });
};

/**
 * Paginated, searchable enrollment list for a course. Joins each enrollment to its field engineer
 * (name / email / languages) so the table renders in one query with no per-row lookups.
 *
 * `search` (optional) filters case-insensitively on the FE's name OR email, in MongoDB. When it is
 * present the FE join runs before pagination (so search spans the whole course); when it is empty
 * the join runs only on the current page slice, keeping large courses fast.
 */
export const getCourseEnrollmentsPage = registerQuery({
  operationName: 'getCourseEnrollmentsPage',
  type: () => CourseEnrollmentPage,
  params: [
    { name: 'courseId', type: () => String },
    { name: 'search', type: () => String },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
  resolve:
    (courseId: string, search?: string, skip = 0, limit = 100) =>
    async (_gqlFields: BaseQLSelectionSet<CourseEnrollmentPage>): Promise<CourseEnrollmentPage> => {
      await getABAC().assert(createLMSPayload({ action: LMSAction.CourseEnrollmentRead }));

      const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const trimmed = (search ?? '').trim();

      const feLookup = {
        $lookup: {
          from: SmartTeamsFieldEngineer._collectionName,
          let: { uid: '$userId' },
          pipeline: [
            { $match: { $expr: { $eq: ['$userId', '$$uid'] }, deletedAt: null } },
            { $project: { name: 1, email: 1, firstLanguage: 1, secondLanguage: 1 } },
          ],
          as: '_fe',
        },
      };
      const feFlatten = { $addFields: { _fe: { $arrayElemAt: ['$_fe', 0] } } };

      const pre: Record<string, unknown>[] = [{ $match: { courseId } }];
      if (trimmed) {
        const regex = { $regex: escapeRegex(trimmed), $options: 'i' };
        pre.push(feLookup, feFlatten, { $match: { $or: [{ '_fe.name': regex }, { '_fe.email': regex }] } });
      }

      const rowsBranch: Record<string, unknown>[] = [{ $sort: { createdAt: -1 } }, { $skip: skip }, { $limit: limit }];
      if (!trimmed) rowsBranch.push(feLookup, feFlatten);

      const [facet] = await col
        .aggregate<{ rows: RawEnrollmentDoc[]; total: { count: number }[] }>([
          ...pre,
          { $facet: { rows: rowsBranch, total: [{ $count: 'count' }] } },
        ])
        .toArray();

      return new CourseEnrollmentPage({
        _id: courseId,
        total: facet?.total?.[0]?.count ?? 0,
        rows: (facet?.rows ?? []).map(toRow),
      });
    },
});
